import React, { useState } from "react";
import Input from "../input";
import {  useForm } from "react-hook-form";
import { Modal } from "antd";
import StepIndicator from "./indicator";
import SupplierDetails from "./supplier-details";
import Kyc from "./kyc";
import Button from "../button";
import Icon from "../../assets/icons";
import { Plus } from "lucide-react";
import { showToast } from "../toast";
import { RingLoader } from "react-spinners";
import useApi from "../hooks/request";
import FIlterSelect from "../filter-select";

interface AddSupplierDialogProps {
  isVisible: boolean;
  onClose: () => void;
  product?: any;
  supplier?: any;
}

type FormStep = "supplierDetails" | "kyc" | "products";

interface Product {
  id: number;
  name: string;
  price: string;
  _id: string;
}

const AddSupplierDialog: React.FC<AddSupplierDialogProps> = ({
  isVisible,
  onClose,
  product,
  supplier,
}) => {
  const [currentStep, setCurrentStep] = useState<FormStep>("supplierDetails");
  const [products, setProducts] = useState<Product[]>([
    { id: Date.now(), name: "", price: "", _id: "" },
  ]);

  // Create a mapping of product IDs to their details for quick lookups
  const productMap = React.useMemo(() => {
    const map: Record<string, { name: string; price: string }> = {};
    if (product && Array.isArray(product)) {
      product.forEach((item: any) => {
        map[item._id] = {
          name: item.name,
          price: item.price || "",
        };
      });
    }
    return map;
  }, [product]);

  const productOptions =
    product?.map((item: any) => ({
      value: item._id,
      label: item.name,
    })) || [];

  const {
    control,
    setValue,
    handleSubmit,
    trigger,
    clearErrors,
    formState: { errors },
  } = useForm({
    mode: "onChange",
  });
  const { loading, makeRequest } = useApi("/suppliers", "POST");

  const handleCreateCustomer = handleSubmit(async (formData) => {
    const form = new FormData();
    form.append("email", formData.email);
    form.append("name", formData.name);
    form.append("contactPerson", formData.contactPerson);
    form.append("phone", formData.phone);
    form.append("businessStreet", formData.businessStreet || "");
    form.append("businessCity", formData.businessCity || "");
    form.append("businessState", formData.businessState || "");
    form.append("supplierType", formData.businessType);
    form.append("businessRegNumber", formData.businessRegNumber);
    form.append("bankName", formData.bankName);
    form.append("bankAccountNumber", formData.bankAccountNumber);
    form.append("bankAccountName", formData.bankAccountName);

    if (formData.businessCertificate) {
      form.append("businessCertificate", formData.businessCertificate);
    }
    if (formData.businessOwnerId) {
      form.append("businessOwnerId", formData.businessOwnerId);
    }

    const validProducts = products.filter((p) => p.name && p.price && p._id);

    if (validProducts.length === 0) {
      showToast("At least one inventory item is required", false, {
        position: "top-right",
      });
      return;
    }

    // Send all required product fields
    validProducts.forEach((product, index) => {
      form.append(`Item[${index}][_id]`, product._id);
    });

    const [response] = await makeRequest(form);
    console.log(response)
    if (response.status) {
      showToast(response.message, true, { position: "top-right" });
      onClose();
      window.location.reload();
    } else {
      showToast(response?.message, false, { position: "top-right" });
    }
  });

  const updateProductField = (
    itemId: number,
    field: keyof Product,
    value: string
  ) => {
    setProducts(
      products.map((item) =>
        item.id === itemId ? { ...item, [field]: value } : item
      )
    );
  };

  // Function to handle product selection
  const handleProductSelection = (itemId: number, productId: string) => {
    const selectedProduct = productMap[productId];
    if (selectedProduct) {
      // Update all relevant fields for the product
      setProducts(
        products.map((item) =>
          item.id === itemId
            ? {
                ...item,
                _id: productId,
                name: selectedProduct.name,
                price: selectedProduct.price,
              }
            : item
        )
      );
    } else {
      // Just update the ID if product details aren't found
      updateProductField(itemId, "_id", productId);
    }
  };

  const addProduct = () => {
    setProducts([
      ...products,
      { id: Date.now(), name: "", price: "", _id: "" },
    ]);
  };

  const removeProduct = (itemId: number) => {
    if (products.length > 1) {
      setProducts(products.filter((product) => product.id !== itemId));
    }
  };

  const validateAndProceed = async () => {
    let isValid = true;

    if (currentStep === "supplierDetails") {
      const fieldsToValidate = [
        "name",
        "businessType",
        "contactPerson",
        "phone",
        "email",
        "bankName",
        "businessCity",
        "bankAccountNumber",
        "bankAccountName",
      ];
      isValid = await trigger(fieldsToValidate);
    } else if (currentStep === "kyc") {
      const fieldsToValidate = [
        "businessRegNumber",
        "businessStreet",
        "businessState",
        "businessCity",
        "businessCertificate",
        "businessOwnerId",
      ];
      isValid = await trigger(fieldsToValidate);
    } else if (currentStep === "products") {
      // Validate all products have name and price
      for (const product of products) {
        if (!product._id) {
          isValid = false;
          showToast("Please select a product", false);
          break;
        }
        if (!product.price) {
          isValid = false;
          showToast("Please enter price for all products", false);
          break;
        }
      }
    }

    if (isValid) {
      if (currentStep === "supplierDetails") {
        setCurrentStep("kyc");
      } else if (currentStep === "kyc") {
        setCurrentStep("products");
      } else if (currentStep === "products") {
        handleCreateCustomer();
      }
    }
  };

  const goBackToPreviousStep = () => {
    if (currentStep === "kyc") {
      setCurrentStep("supplierDetails");
    } else if (currentStep === "products") {
      setCurrentStep("kyc");
    }
  };

  const renderProductsStep = () => {
    return (
      <div className="space-y-4">
        {products.map((product, index) => (
          <div key={product.id} className="grid grid-cols-3 gap-4 items-center">
            <div>
              <FIlterSelect
                name={`product_id_${product.id}`}
                options={productOptions}
                className="w-full"
                label="Item Name"
                onChange={(selectedValue) => {
                  handleProductSelection(product.id, selectedValue);
                }}
                value={product._id}
              />
            </div>

            <div>
              <Input
                value={product.price}
                className="w-full"
                label={"Unit Price"}
                placeholder="Enter price"
                type="number"
                onChange={(e) =>
                  updateProductField(product.id, "price", e.target.value)
                }
              />
            </div>

            <div className="flex justify-end pt-1">
              {products.length > 1 && (
                <button
                  type="button"
                  onClick={() => removeProduct(product.id)}
                  className="text-red-500 hover:text-red-700"
                >
                  <Icon name="trashSimple" />
                </button>
              )}
            </div>
          </div>
        ))}

        <button
          type="button"
          className="flex items-center justify-center gap-2 text-green-600 font-medium mt-4"
          onClick={addProduct}
        >
          <Plus size={16} />
          Add New Item
        </button>
      </div>
    );
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case "supplierDetails":
        return (
          <SupplierDetails
            control={control}
            clearErrors={clearErrors}
            errors={errors}
            supplier={supplier}
          />
        );
      case "kyc":
        return (
          <Kyc
            control={control}
            setValue={setValue}
            clearErrors={clearErrors}
            errors={errors}
            supplier={supplier}
          />
        );
      case "products":
        return renderProductsStep();
      default:
        return null;
    }
  };

  const renderFooterButtons = () => {
    // Footer button logic remains the same
    if (currentStep === "supplierDetails") {
      return (
        <div className="flex gap-3 py-2 mt-4">
          <Button
            type="button"
            className="hover:bg-green-700 flex items-center"
            onClick={validateAndProceed}
            variant="primary"
            size="lg"
          >
            <Icon name="noteIcon" />
            Proceed to KYC
          </Button>
        </div>
      );
    } else if (currentStep === "kyc") {
      return (
        <div className="flex gap-2 items-center text-center py-2 mt-4">
          <button
            type="button"
            className="bg-[#E8F2E8] w-full rounded-full hover:bg-gray-200 px-7 py-2 text-[#047401] flex font-medium"
            onClick={goBackToPreviousStep}
          >
            <Icon name="backIcon" />
            Back to Supplier Details
          </button>
          <Button
            type="button"
            size="lg"
            variant="primary"
            className="hover:bg-green-700 text-white flex"
            onClick={validateAndProceed}
          >
            <Icon name="noteIcon" />
            Proceed to Products
          </Button>
        </div>
      );
    } else if (currentStep === "products") {
      return (
        <div className="flex justify-between gap-2 py-2 mt-4">
          <button
            type="button"
            className="bg-[#E8F2E8] w-full rounded-full hover:bg-gray-200 pl-20 py-2 text-[#047401] flex font-medium"
            onClick={goBackToPreviousStep}
          >
            <Icon name="backIcon" />
            Back to KYC
          </button>
          <Button
            type="button"
            variant="primary"
            size="lg"
            className="hover:bg-green-700 text-white flex items-center"
            onClick={validateAndProceed}
          >
            {loading ? (
              <RingLoader color="#ffffff" loading={loading} size={20} />
            ) : (
              <div className="flex gap-3">
                Submit for Approval <Icon name="noteIcon" />
              </div>
            )}
          </Button>
        </div>
      );
    }

    return null;
  };

  return (
    <Modal
      visible={isVisible}
      onCancel={onClose}
      width="80%"
        centered={true}
        footer={null}
        style={{
          padding: 0,
          margin: 0,
          maxWidth: "1200px",
          height: "auto"
        }}
    >
      <h1 className="text-2xl font-bold -mt-2 mb-4">Add Supplier</h1>
      <StepIndicator
        currentStep={currentStep}
        setCurrentStep={setCurrentStep}
      />
      <form onSubmit={handleCreateCustomer}>
        <div className="grid gap-4">{renderStepContent()}</div>
        {renderFooterButtons()}
      </form>
    </Modal>
  );
};

export default AddSupplierDialog;
